
import { toast } from "@/hooks/use-toast";
import { refreshMetadata } from "./metadataRefresher";

/**
 * Utilities for social media sharing with improved cache handling
 * and domain-specific optimizations for selahtools.com
 */

// Cache buster timestamp to force refresh of metadata
const cacheBuster = Date.now().toString();

// Base URL of the application
const getBaseUrl = () => {
  // Return the canonical URL for the site
  return "https://selahtools.com";
};

// Get the current canonical URL with path
const getCurrentCanonicalUrl = () => {
  const baseUrl = getBaseUrl();
  const path = window.location.pathname;
  return `${baseUrl}${path}`;
};

/**
 * Share content via email
 */
export const shareViaEmail = (subject: string, body: string) => {
  const emailSubject = encodeURIComponent(subject);
  const emailBody = encodeURIComponent(body);
  
  window.location.href = `mailto:?subject=${emailSubject}&body=${emailBody}`;
  
  toast({
    title: "Email Client Opened",
    description: "Your email client should be opening to share this content.",
  });
};

/**
 * Share content via text message (SMS)
 */
export const shareViaSMS = (text: string) => {
  const smsText = encodeURIComponent(text);
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  
  if (isMobile) {
    window.location.href = `sms:?&body=${smsText}`;
  } else {
    navigator.clipboard.writeText(text).then(() => {
      toast({
        title: "Copied to Clipboard",
        description: "The content has been copied to your clipboard as SMS sharing is only available on mobile devices.",
      });
    });
  }
};

/**
 * Share to Facebook with cache busting and proper URL handling
 */
export const shareToFacebook = (customUrl?: string) => {
  // First refresh metadata to ensure Facebook gets fresh info
  refreshMetadata();
  
  // Use custom URL or current canonical URL with cache busting
  let url = customUrl;
  
  if (!url) {
    // Add cache buster to the URL
    url = `${getCurrentCanonicalUrl()}?_cb=${cacheBuster}`;
  }
  
  // Ensure URL is properly encoded
  const encodedUrl = encodeURIComponent(url);
  const facebookShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
  
  // Log sharing to help with debugging
  console.log("Sharing to Facebook:", { url, encodedUrl, facebookShareUrl });
  
  // Open the share dialog
  window.open(facebookShareUrl, '_blank', 'width=600,height=400');
  
  toast({
    title: "Sharing to Facebook",
    description: "A Facebook share dialog should have opened.",
  });
  
  // Return the Facebook URL for testing purposes
  return facebookShareUrl;
};

/**
 * Force refresh of social media cache for current page
 * Opens Facebook's debugger to clear cache
 */
export const refreshSocialCache = async () => {
  try {
    // First refresh all metadata
    refreshMetadata();
    
    toast({
      title: "Refreshing Social Cache",
      description: "Opening Facebook Debugger to refresh social media metadata cache...",
    });
    
    // Get the current canonical URL
    const currentUrl = getCurrentCanonicalUrl();
    
    // Open Facebook's debugger with the current URL
    const fbDebuggerUrl = `https://developers.facebook.com/tools/debug/?q=${encodeURIComponent(currentUrl)}`;
    
    window.open(fbDebuggerUrl, '_blank');
    
    return true;
  } catch (error) {
    console.error("Error refreshing social cache:", error);
    return false;
  }
};

/**
 * Update all Open Graph meta tags with fresh data
 */
export const updateOpenGraphMetaTags = () => {
  try {
    // Update the basic OG tags
    document.querySelector('meta[property="og:url"]')?.setAttribute('content', getCurrentCanonicalUrl());
    
    // Add timestamp to image URLs to force refresh
    const timestamp = Date.now().toString();
    document.querySelectorAll('meta[property^="og:image"]').forEach(tag => {
      const content = tag.getAttribute('content');
      if (content) {
        // Remove any existing timestamp query params
        const baseUrl = content.split('?')[0];
        tag.setAttribute('content', `${baseUrl}?v=${timestamp}`);
      }
    });
    
    // Also update Twitter image
    document.querySelectorAll('meta[name^="twitter:image"]').forEach(tag => {
      const content = tag.getAttribute('content');
      if (content) {
        // Remove any existing timestamp query params
        const baseUrl = content.split('?')[0];
        tag.setAttribute('content', `${baseUrl}?v=${timestamp}`);
      }
    });
    
    console.log("Open Graph meta tags updated with timestamp:", timestamp);
    return true;
  } catch (error) {
    console.error("Error updating Open Graph meta tags:", error);
    return false;
  }
};
